import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { mockRechargeRecords } from './mockData';
import SearchFiltersComponent from './components/SearchFilters';
import type { RechargeRecord, SearchFilters } from './types';

export default function RechargeRecordsPage() {
  const [records] = useState<RechargeRecord[]>(mockRechargeRecords);
  const [filteredRecords, setFilteredRecords] = useState<RechargeRecord[]>(mockRechargeRecords);

  const handleSearch = (filters: SearchFilters) => {
    let filtered = [...records];

    if (filters.searchValue) {
      filtered = filtered.filter((record) => {
        const value = filters.searchValue.toLowerCase();
        switch (filters.searchType) {
          case 'orderNo':
            return record.orderNo.toLowerCase().includes(value);
          case 'userId':
            return record.userId.toLowerCase().includes(value);
          case 'userName':
            return record.userName.toLowerCase().includes(value);
          case 'operatorId':
            return record.operatorId.toLowerCase().includes(value);
          case 'operatorName':
            return record.operatorName.toLowerCase().includes(value);
          default:
            return true;
        }
      });
    }

    if (filters.rechargeTypes.length > 0) {
      filtered = filtered.filter((record) => filters.rechargeTypes.includes(record.rechargeType));
    }

    if (filters.userTypes.length > 0) {
      filtered = filtered.filter((record) => filters.userTypes.includes(record.userType));
    }

    if (filters.rechargeMethods.length > 0) {
      filtered = filtered.filter((record) => filters.rechargeMethods.includes(record.rechargeMethod));
    }

    if (filters.paymentMethods.length > 0) {
      filtered = filtered.filter((record) => filters.paymentMethods.includes(record.paymentMethod));
    }

    if (filters.startTime) {
      filtered = filtered.filter((record) => record.rechargeTime >= filters.startTime.replace('T', ' '));
    }

    if (filters.endTime) {
      filtered = filtered.filter((record) => record.rechargeTime <= filters.endTime.replace('T', ' '));
    }

    setFilteredRecords(filtered);
  };

  const getRechargeTypeColor = (type: string) => {
    switch (type) {
      case '充值':
        return 'text-green-600 bg-green-50';
      case '红冲':
        return 'text-red-600 bg-red-50';
      case '退款':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatAmount = (amount: number) => {
    const prefix = amount >= 0 ? '+' : '';
    return `${prefix}¥${amount.toLocaleString()}`;
  };

  const formatAmountColor = (amount: number) => {
    return amount >= 0 ? 'text-green-600' : 'text-red-600';
  };

  return (
    <MainLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">充值记录</h1>
        <p className="text-sm text-gray-500 mt-1">查看和管理所有充值记录</p>
      </div>

      <SearchFiltersComponent onSearch={handleSearch} />

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  充值ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  充值订单号
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  充值类型
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  用户类型
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  用户ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  用户名称
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  充值金额
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  转为积分
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  赠送金额
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  实付金额
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  充值方式
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  支付方式
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  操作用户ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  操作用户名称
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  充值时间
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecords.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.rechargeId}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.orderNo}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRechargeTypeColor(record.rechargeType)}`}>
                      {record.rechargeType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.userType}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.userId}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.userName}</td>
                  <td className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${formatAmountColor(record.rechargeAmount)}`}>
                    {formatAmount(record.rechargeAmount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {record.convertedPoints > 0 ? record.convertedPoints.toLocaleString() : '-'}
                  </td>
                  <td className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${formatAmountColor(record.giftAmount)}`}>
                    {record.giftAmount !== 0 ? formatAmount(record.giftAmount) : '-'}
                  </td>
                  <td className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${formatAmountColor(record.actualAmount)}`}>
                    {formatAmount(record.actualAmount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.rechargeMethod}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.paymentMethod}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.operatorId}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.operatorName}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{record.rechargeTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRecords.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-file-list-line text-4xl text-gray-300"></i>
            <p className="mt-2 text-sm text-gray-500">暂无充值记录</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
